import React, {createContext, useContext, useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';
import moment from 'moment/moment';
import * as _ from 'lodash';
import * as actions from '../_redux/transfersByAgentActions';
import {LoadingDialog} from '../../../../_metronic/_partials/controls';
import * as columnFormatters from '../../_common/column-formatters';
import {formattingPrice} from '../../_common/utils/format';
import {getPaymentTypeName} from '../utils';

const TransfersByAgentContext = createContext({});

export const useTransfersByAgentContext = () => useContext(TransfersByAgentContext);

export const TransfersByAgentProvider = ({children}) => {
  const {agentId} = useParams();
  const intl = useIntl();
  const dispatch = useDispatch();

  const getTransfersByAgent = () => {
    if (agentId) {
      dispatch(actions.getTransfersByAgent(parseInt(agentId)));
    }
  };

  useEffect(() => {getTransfersByAgent();}, [agentId]);

  const deleteTransfer = (transferID, paycheck) => {
    if (transferID) {
      dispatch(actions.deleteTransfer(transferID, paycheck, agentId));
      getTransfersByAgent();
    }
  };

  const unsplitReceiptLine = (receiptLineId) => {
    if (receiptLineId) {
      dispatch(actions.unsplitReceiptLine(receiptLineId, agentId));
      getTransfersByAgent();
    }
  };

  const {isLoading, data} = useSelector(
    (state) => ({
      isLoading: state.transfersByAgent.loading,
      data: state.transfersByAgent.data
    }),
    shallowEqual
  );

  const columns = useMemo(() => [
    {
      accessor: 'checkbox',
      Header: ''
    },
    {
      accessor: 'splitLine',
      Header: ''
    },
    {
      accessor: 'receiptID',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-receiptID',
        defaultMessage: 'Receipt ID'
      })
    },
    {
      accessor: 'customer',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-customer',
        defaultMessage: 'Customer'
      })
    },
    {
      accessor: 'receiptDate',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-receiptDate',
        defaultMessage: 'Receipt date'
      })
    },
    {
      accessor: 'paymentType',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-paymentType',
        defaultMessage: 'Payment type'
      })
    },
    {
      accessor: 'amount',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-amount',
        defaultMessage: 'Amount'
      })
    },
  ], []);
  const columnsTransfers = useMemo(() => [
    {
      accessor: 'transferPaycheck',
      Header: ''
    },
    {
      accessor: 'receiptID',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-receiptID',
        defaultMessage: 'Receipt ID'
      })
    },
    {
      accessor: 'customer',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-customer',
        defaultMessage: 'Customer'
      })
    },
    {
      accessor: 'receiptDate',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-receiptDate',
        defaultMessage: 'Receipt date'
      })
    },
    {
      accessor: 'paymentType',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-paymentType',
        defaultMessage: 'Payment type'
      })
    },
    {
      accessor: 'amount',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-amount',
        defaultMessage: 'Amount'
      })
    },
    {
      accessor: 'actions',
      Header: intl.formatMessage({
        id: 'transfersByAgent-transfers-column-actions',
        defaultMessage: 'Actions'
      })
    },
  ], []);

  const unpaidReceiptLines = Boolean(data) ? data.unpaidReceiptLines : [];
  const dataUnpaidReceiptLines = useMemo(() => [
    ...unpaidReceiptLines.map((item) => {
      const customer = [
        _.get(item, 'receipt.customer._id', ''),
        '-',
        _.get(item, 'receipt.customer.name.first', ''),
        _.get(item, 'receipt.customer.name.last', '')
      ].filter(Boolean).join(' ');
      const amount = item.amount && typeof item.amount === 'number' ? item.amount.toFixed(2) : item.amount;
      const originalAmount = item.originalAmount && typeof item.originalAmount === 'number' ? item.originalAmount.toFixed(2) : item.originalAmount;
      const amountValue = item?.splitNum
        ? [formattingPrice(amount, true), formattingPrice(originalAmount, true)].filter(Boolean).join('/')
        : formattingPrice(amount, true);
      return {
        _id: item._id,
        checkbox: columnFormatters.TransfersCheckboxColumnFormatter(item._id),
        splitLine: columnFormatters.SplitLineColumnFormatter(item),
        receiptID: columnFormatters.TransfersNormalColumnFormatter(item._id),
        customer: columnFormatters.TransfersNormalColumnFormatter(customer),
        receiptDate: columnFormatters.TransfersDateColumnFormatter(item.dueDate),
        paymentType: columnFormatters.TransfersNormalColumnFormatter(getPaymentTypeName(item.paymentType)),
        amount: columnFormatters.TransfersNormalColumnFormatter(amountValue),
      };
    })
  ], [unpaidReceiptLines]);

  const hiddenReceiptLines = Boolean(data) ? data.hiddenReceiptLines : [];
  const dataHiddenReceiptLines = useMemo(() => [
    ...hiddenReceiptLines.map((item) => {
      const customer = [
        _.get(item, 'receipt.customer._id', ''),
        '-',
        _.get(item, 'receipt.customer.name.first', ''),
        _.get(item, 'receipt.customer.name.last', '')
      ].filter(Boolean).join(' ');
      const amount = item.amount && typeof item.amount === 'number' ? item.amount.toFixed(2) : item.amount;
      const originalAmount = item.originalAmount && typeof item.originalAmount === 'number' ? item.originalAmount.toFixed(2) : item.originalAmount;
      const amountValue = item?.splitNum
        ? [formattingPrice(amount, true), formattingPrice(originalAmount, true)].filter(Boolean).join('/')
        : formattingPrice(amount, true);
      return {
        _id: item._id,
        checkbox: columnFormatters.TransfersCheckboxColumnFormatter(item._id),
        splitLine: columnFormatters.SplitLineColumnFormatter(item),
        receiptID: columnFormatters.TransfersNormalColumnFormatter(item._id),
        customer: columnFormatters.TransfersNormalColumnFormatter(customer),
        receiptDate: columnFormatters.TransfersDateColumnFormatter(item.dueDate),
        paymentType: columnFormatters.TransfersNormalColumnFormatter(getPaymentTypeName(item.paymentType)),
        amount: columnFormatters.TransfersNormalColumnFormatter(amountValue),
      };
    })
  ], [hiddenReceiptLines]);

  const splitReceiptLines = Boolean(data) ? data.splitReceiptLines : [];
  const dataSplitReceiptLines = useMemo(() => [
    ...splitReceiptLines.map((item) => {
      const customer = [
        _.get(item, 'receipt.customer._id', ''),
        '-',
        _.get(item, 'receipt.customer.name.first', ''),
        _.get(item, 'receipt.customer.name.last', '')
      ].filter(Boolean).join(' ');
      const amount = item.amount && typeof item.amount === 'number' ? item.amount.toFixed(2) : item.amount;
      const amountValue = formattingPrice(amount, true);
      return {
        _id: item._id,
        checkbox: columnFormatters.BoldColumnFormatter(''),
        splitLine: columnFormatters.UnSplitLineColumnFormatter(item._id, unsplitReceiptLine),
        receiptID: columnFormatters.TransfersNormalColumnFormatter(item._id),
        customer: columnFormatters.TransfersNormalColumnFormatter(customer),
        receiptDate: columnFormatters.TransfersDateColumnFormatter(item.dueDate),
        paymentType: columnFormatters.TransfersNormalColumnFormatter(getPaymentTypeName(item.paymentType)),
        amount: columnFormatters.TransfersNormalColumnFormatter(amountValue),
      };
    })
  ], [splitReceiptLines]);

  const transfersLines = Boolean(data) ? data.transfers : [];
  const dataTransfers = useMemo(() => [
    ...transfersLines
      .map(item => {
        if (item.paycheck.splits > 1) {
          return new Array(item.paycheck.splits)
            .fill(item)
            .map((item, index) => {
              const month = Math.min(item.paycheck.date.month + index, 12);
              const year = (item.paycheck.date.month + index) > 12 ? item.paycheck.date.year + 1 : item.paycheck.date.year;

              return {
                ...item,
                paycheck: {
                  ...item.paycheck,
                  date: {month, year},
                },
                splitNum: index + 1,
              }
            });
        } else {
          return [item];
        }
      })
      .flat()
      .map((item, index) => {
      const customersFromLines = item.lines.map(line => {
        return [
          _.get(line, 'receipt.customer._id', ''),
          '-',
          _.get(line, 'receipt.customer.name.first', ''),
          _.get(line, 'receipt.customer.name.last', '')
        ].filter(Boolean).join(' ');
      });
      const customersFromCustomLines = item.customLines.map(item => item.customer ? item.customer : '');
      const customers = [...customersFromLines, ...customersFromCustomLines];

      const datesFromLines = item.lines.map(line => {
        const {receipt} = line;
        return receipt?.date ? moment(receipt.date).format('DD-MM-YYYY') : '';
      });
      const datesFromCustomLines = item.customLines.map(item => item.creationDate ? item.creationDate : '');
      const receiptDates = [...datesFromLines, ...datesFromCustomLines];

      const paymentTypesFromLines = item.lines.map(line => getPaymentTypeName(line.paymentType));
      const paymentTypesFromCustomLines = item.customLines.map(item => item.paymentType ? item.paymentType : '');
      const paymentTypes = [...paymentTypesFromLines, ...paymentTypesFromCustomLines];

      const amountsFromLines = item.lines.map(line => {
        return [
          formattingPrice(line.amount ? parseFloat(line.amount).toFixed(2) : 0, true),
          formattingPrice(line.originalAmount ? parseFloat(line.originalAmount).toFixed(2) : 0, true),
        ].filter(Boolean).join('/');
      });
      const amountsFromCustomLines = item.customLines.map(customLine => {
        const price = customLine.amount ? customLine.amount.toFixed(2) : 0;
        return formattingPrice(price, true);
      });
      const amounts = [...amountsFromLines, ...amountsFromCustomLines];
      const canDelete = index === 0;
      const transfersMonthYear = [
        'משכורת ',
        _.get(item, 'paycheck.date.month', ''),
        '.',
        _.get(item, 'paycheck.date.year', '')
      ].filter(Boolean).join('');

      return {
        transferPaycheck: columnFormatters.TransfersPaycheckColumnFormatter(item.datetime, transfersMonthYear, item.splitNum),
        receiptID: columnFormatters.TransfersSeveralItemsColumnFormatter(item.lineIds),
        customer: columnFormatters.TransfersSeveralItemsColumnFormatter(customers, false, false),
        receiptDate: columnFormatters.TransfersSeveralItemsColumnFormatter(receiptDates, false, false),
        paymentType: columnFormatters.TransfersSeveralItemsColumnFormatter(paymentTypes, false, false),
        amount: columnFormatters.TransfersSeveralItemsColumnFormatter(amounts, false, false),
        actions: columnFormatters.TransfersActionsColumnFormatter(item, data?.fullName || '', canDelete),
      };
    })
  ], [transfersLines]);

  const currentLastPaycheck = _.get(
    data,
    'transfers[0].lastPaycheck.date',
    {year: moment().year(), month: moment().month() + 1}
  );

  const generalAdminNotes = _.get(data, 'adminNotes.adminNotes', '');

  const isTaxAdjustmentOrForm101Absent = !data?.form101FileExists || !data?.taxAdjustmentFileExists;

  return (
    <TransfersByAgentContext.Provider
      value={{
        columns,
        columnsTransfers,
        dataUnpaidReceiptLines,
        dataHiddenReceiptLines,
        dataSplitReceiptLines,
        dataTransfers,
        notifications: {
          form101FileExists: data?.form101FileExists ? data.form101FileExists : false,
          taxAdjustmentFileExists: data?.taxAdjustmentFileExists ? data.taxAdjustmentFileExists : false,
          otherIncomeExists: data?.otherIncomeExists ? data.otherIncomeExists : false,
          affiliate: data?.affiliate ? data.affiliate : '',
          discounts: data?.discounts ? data.discounts : [],
        },
        tamalData: {
          expectedAvgIncome: data?.avgIncome.expectedAvgIncome ? data.avgIncome.expectedAvgIncome : '',
          realAvgIncome: data?.avgIncome.realAvgIncome ? data.avgIncome.realAvgIncome : '',
          tamalID: data?.avgIncome.tamalID ? data.avgIncome.tamalID : '',
        },
        adminNotes: data?.adminNotes ? data.adminNotes.adminNotes : '',
        getTransfersByAgent,
        deleteTransfer,
        agentId,
        userId: data?.userID || '',
        fullName: data?.fullName || '',
        unpaidReceiptLines,
        transfersLines,
        currentLastPaycheck,
        isTaxAdjustmentOrForm101Absent,
        generalAdminNotes,
        history: data?.history || {allTime: {}, byMonth: {}, byYear: {}},
      }}
    >
      {children}
      <LoadingDialog isLoading={isLoading}/>
    </TransfersByAgentContext.Provider>
  );
};